import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api/client";
import { useAuth } from "../context/AuthContext";

const RULE_OPTIONS = [
  { value: "",                      label: "— No rule —" },
  { value: "equals",                label: "Equals expected value" },
  { value: "not_equals",            label: "Not equals" },
  { value: "contains",              label: "Answer contains" },
  { value: "greater_than",          label: "Greater than" },
  { value: "less_than",             label: "Less than" },
  { value: "between",               label: "Between (min,max)" },
  { value: "required_only",         label: "Any answer" },
  { value: "date_on_or_after",      label: "Date on or after" },
  { value: "date_before",           label: "Date before" },
  { value: "date_within_last_days", label: "Date within last N days" },
];

function rulesForType(questionType) {
  const numeric = ["equals", "not_equals", "greater_than", "less_than", "between", "required_only"];
  const date = ["date_on_or_after", "date_before", "date_within_last_days", "required_only"];
  const text = ["equals", "not_equals", "contains", "required_only"];
  const basic = ["equals", "not_equals", "required_only"];
  let allowed;
  switch (questionType) {
    case "numeric": allowed = numeric; break;
    case "date":    allowed = date;    break;
    case "text":    allowed = text;    break;
    default:        allowed = basic;
  }
  return RULE_OPTIONS.filter(o => o.value === "" || allowed.includes(o.value));
}

const SEVERITY_OPTIONS = ["minor", "major", "critical"];

const NEEDS_EV = new Set([
  "equals","not_equals","contains","greater_than","less_than","between",
  "date_on_or_after","date_before","date_within_last_days",
]);


export default function TemplateRules() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // Per-question local state: { [questionId]: { compliance_rule, expected_value, severity, saving, err } }
  const [rules, setRules] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  const [allSaved, setAllSaved] = useState(false);

  useEffect(() => {
    if (!templateId || !token) return;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const data = await api(`/india/templates/${templateId}`, { token });
        setTemplate(data);
        // Initialise rules from what's already stored
        const initial = {};
        for (const sec of data.sections || []) {
          for (const q of sec.questions || []) {
            // For numeric and text questions, "yes" is a stale default from yes_no — clear it
            const staleYesNo = (q.type === "numeric" || q.type === "text") && q.expected_value === "yes";
            initial[q.id] = {
              compliance_rule: q.compliance_rule || "",
              expected_value:  staleYesNo ? "" : (q.expected_value || ""),
              severity:        q.severity        || "major",
              saving: false,
              err: null,
            };
          }
        }
        setRules(initial);
      } catch (e) {
        setErr(e?.message || "Failed to load template");
      } finally {
        setLoading(false);
      }
    })();
  }, [templateId, token]);

  function updateRule(qid, patch) {
    setRules(prev => ({
      ...prev,
      [qid]: { ...prev[qid], ...patch },
    }));
    setAllSaved(false);
  }

  async function saveQuestion(qid) {
    const r = rules[qid];
    if (!r) return;
    updateRule(qid, { saving: true, err: null });
    try {
      await api(`/india/templates/${templateId}/questions/${qid}`, {
        token,
        method: "PATCH",
        body: {
          compliance_rule: r.compliance_rule || null,
          expected_value:  NEEDS_EV.has(r.compliance_rule) ? (r.expected_value || null) : null,
          severity:        r.severity || "major",
        },
      });
      updateRule(qid, { saving: false, err: null });
    } catch (e) {
      updateRule(qid, { saving: false, err: e?.message || "Save failed" });
    }
  }

  async function saveAll() {
    setSavingAll(true);
    setErr(null);
    const allQids = Object.keys(rules).map(Number);
    for (const qid of allQids) {
      await saveQuestion(qid);
    }
    setSavingAll(false);
    // Check if everything is set
    const anyMissing = allQids.some(qid => !rules[qid]?.compliance_rule);
    setAllSaved(!anyMissing);
  }

  function getMissingCount() {
    return Object.values(rules).filter(r => !r.compliance_rule).length;
  }

  if (loading) return <div className="pt-20 px-6 text-sm text-gray-500">Loading…</div>;
  if (err) return <div className="pt-20 px-6 text-sm text-red-600">{err}</div>;
  if (!template) return null;

  const missingCount = getMissingCount();
  const totalCount = Object.keys(rules).length;

  return (
    <div className="pt-8 px-6 max-w-4xl mx-auto pb-16">
      <div className="mb-6">
        <div className="text-xs text-gray-500 mb-1">Templates → Set compliance rules</div>
        <h1 className="text-xl font-semibold">{template.name}</h1>
        {template.code && (
          <div className="text-xs text-gray-500 font-mono mt-0.5">{template.code} v{template.version}</div>
        )}
      </div>

      {/* Status banner */}
      {missingCount > 0 ? (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <span className="font-semibold">{missingCount} of {totalCount} questions</span> still need a compliance rule.
          Without rules, failing answers won't create findings.
        </div>
      ) : allSaved ? (
        <div className="mb-6 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          ✓ All questions have compliance rules saved. You can now use this template for inspections.
        </div>
      ) : (
        <div className="mb-6 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          ✓ All questions have compliance rules set. Save all to confirm.
        </div>
      )}

      {/* Sections + questions */}
      <div className="space-y-6">
        {template.sections?.map((sec) => (
          <div key={sec.id}>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              {sec.title}
            </div>
            <div className="space-y-3">
              {sec.questions?.map((q) => {
                const r = rules[q.id] || {};
                return (
                  <div key={q.id} className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-start gap-3 mb-3">
                      <span className="shrink-0 font-mono text-xs text-gray-400 bg-gray-100 rounded px-1.5 py-0.5 mt-0.5">
                        {q.code || "—"}
                      </span>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-800">{q.text}</div>
                        <div className="text-xs text-gray-400 mt-0.5 font-mono">{q.type}</div>
                      </div>
                      {/* Status indicator */}
                      {r.compliance_rule ? (
                        <span className="shrink-0 text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-0.5">
                          ✓ set
                        </span>
                      ) : (
                        <span className="shrink-0 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                          ⚠ missing
                        </span>
                      )}
                    </div>

                    <div className="space-y-3">

                      {/* yes_no and yes_no_na: expected answer dropdown */}
                      {(q.type === "yes_no" || q.type === "yes_no_na") && (
                        <div className="flex flex-wrap gap-3 items-end">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Expected answer</label>
                            <select
                              value={r.compliance_rule === "required_only" ? "either" : (r.expected_value || "yes")}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === "either") {
                                  updateRule(q.id, { compliance_rule: "required_only", expected_value: "" });
                                } else {
                                  updateRule(q.id, { compliance_rule: "equals", expected_value: val });
                                }
                              }}
                              className="border rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-gray-400"
                            >
                              <option value="yes">Yes</option>
                              <option value="no">No</option>
                              <option value="either">Either (any answer passes)</option>
                            </select>
                          </div>
                          {r.compliance_rule !== "required_only" && (
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Severity</label>
                              <select
                                value={r.severity || "major"}
                                onChange={(e) => updateRule(q.id, { severity: e.target.value })}
                                className="border rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-gray-400"
                              >
                                {SEVERITY_OPTIONS.map(s => (
                                  <option key={s} value={s}>{s}</option>
                                ))}
                              </select>
                            </div>
                          )}
                          <div className="flex flex-col items-start gap-1">
                            <label className="block text-xs text-gray-500 mb-1 invisible">Save</label>
                            <button type="button" onClick={() => saveQuestion(q.id)} disabled={r.saving}
                              className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50 transition">
                              {r.saving ? "Saving…" : "Save"}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* single_choice and multi_choice: checkboxes per option */}
                      {(q.type === "single_choice" || q.type === "multi_choice") && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-xs text-gray-400">
                            <span className="w-4 shrink-0" />
                            <span className="flex-1">Choice</span>
                            <span className="w-20 text-center">Expected?</span>
                          </div>
                          {(q.options || []).length === 0 && (
                            <div className="text-xs text-gray-400 italic">No options saved for this question.</div>
                          )}
                          {(q.options || []).map((opt, idx) => {
                            const expectedVals = r.expected_value
                              ? r.expected_value.split(",").map(v => v.trim()).filter(Boolean)
                              : [];
                            const isChecked = expectedVals.includes(opt.value);
                            return (
                              <div key={opt.id || idx} className="flex items-center gap-2">
                                <span className="text-xs text-gray-400 w-4 shrink-0">{idx + 1}.</span>
                                <span className="flex-1 text-sm border rounded-lg px-2 py-1.5 bg-gray-50 text-gray-700">
                                  {opt.label || opt.value}
                                </span>
                                <div className="w-20 flex justify-center">
                                  <input
                                    type={q.type === "single_choice" ? "radio" : "checkbox"}
                                    checked={isChecked}
                                    onChange={() => {
                                      let next;
                                      if (q.type === "single_choice") {
                                        next = isChecked ? "" : opt.value;
                                      } else {
                                        next = isChecked
                                          ? expectedVals.filter(v => v !== opt.value).join(",")
                                          : [...expectedVals, opt.value].join(",");
                                      }
                                      updateRule(q.id, {
                                        compliance_rule: next ? "equals" : "",
                                        expected_value: next,
                                      });
                                    }}
                                    className="cursor-pointer"
                                  />
                                </div>
                              </div>
                            );
                          })}
                          <div className="flex flex-wrap gap-3 items-end pt-1">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Severity</label>
                              <select
                                value={r.severity || "major"}
                                onChange={(e) => updateRule(q.id, { severity: e.target.value })}
                                className="border rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-gray-400"
                              >
                                {SEVERITY_OPTIONS.map(s => (
                                  <option key={s} value={s}>{s}</option>
                                ))}
                              </select>
                            </div>
                            <div className="flex flex-col items-start gap-1">
                              <label className="block text-xs text-gray-500 mb-1 invisible">Save</label>
                              <button type="button" onClick={() => saveQuestion(q.id)} disabled={r.saving}
                                className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50 transition">
                                {r.saving ? "Saving…" : "Save"}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* date: rule dropdown + type-appropriate input */}
                      {q.type === "date" && (
                        <div className="flex flex-wrap gap-3 items-end">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Compliance rule</label>
                            <select
                              value={r.compliance_rule || ""}
                              onChange={(e) => updateRule(q.id, { compliance_rule: e.target.value, expected_value: "" })}
                              className="border rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-gray-400"
                            >
                              <option value="">— No rule —</option>
                              <option value="date_on_or_after">Date on or after</option>
                              <option value="date_before">Date before</option>
                              <option value="date_within_last_days">Date within last N days</option>
                              <option value="required_only">Any answer</option>
                            </select>
                          </div>
                          {r.compliance_rule === "date_within_last_days" && (
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Number of days</label>
                              <input
                                type="number"
                                min="1"
                                placeholder="e.g. 365"
                                value={r.expected_value || ""}
                                onChange={(e) => updateRule(q.id, { expected_value: e.target.value })}
                                className="border rounded-lg px-2 py-1.5 text-sm w-32 focus:outline-none focus:ring-1 focus:ring-gray-400"
                              />
                            </div>
                          )}
                          {(r.compliance_rule === "date_on_or_after" || r.compliance_rule === "date_before") && (
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Date</label>
                              <input
                                type="date"
                                value={r.expected_value || ""}
                                onChange={(e) => updateRule(q.id, { expected_value: e.target.value })}
                                className="border rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                              />
                            </div>
                          )}
                          {r.compliance_rule && r.compliance_rule !== "required_only" && (
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Severity</label>
                              <select
                                value={r.severity || "major"}
                                onChange={(e) => updateRule(q.id, { severity: e.target.value })}
                                className="border rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-gray-400"
                              >
                                {SEVERITY_OPTIONS.map(s => (
                                  <option key={s} value={s}>{s}</option>
                                ))}
                              </select>
                            </div>
                          )}
                          <div className="flex flex-col items-start gap-1">
                            <label className="block text-xs text-gray-500 mb-1 invisible">Save</label>
                            <button type="button" onClick={() => saveQuestion(q.id)} disabled={r.saving}
                              className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50 transition">
                              {r.saving ? "Saving…" : "Save"}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* numeric and text: rule dropdown + expected value */}
                      {(q.type === "numeric" || q.type === "text") && (
                        <div className="flex flex-wrap gap-3 items-end">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Compliance rule</label>
                            <select
                              value={r.compliance_rule || ""}
                              onChange={(e) => updateRule(q.id, { compliance_rule: e.target.value, expected_value: "" })}
                              className="border rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-gray-400"
                            >
                              {rulesForType(q.type).map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </div>
                          {NEEDS_EV.has(r.compliance_rule) && (
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">
                                {r.compliance_rule === "between" ? "Min, Max" : "Expected value"}
                              </label>
                              <input
                                type="text"
                                value={r.expected_value || ""}
                                onChange={(e) => updateRule(q.id, { expected_value: e.target.value })}
                                placeholder={
                                  r.compliance_rule === "between" ? "e.g. 0,100"
                                  : q.type === "numeric" ? "e.g. 1"
                                  : "value"
                                }
                                className="border rounded-lg px-2 py-1.5 text-sm w-40 focus:outline-none focus:ring-1 focus:ring-gray-400"
                              />
                            </div>
                          )}
                          {r.compliance_rule && r.compliance_rule !== "required_only" && (
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Severity</label>
                              <select
                                value={r.severity || "major"}
                                onChange={(e) => updateRule(q.id, { severity: e.target.value })}
                                className="border rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-gray-400"
                              >
                                {SEVERITY_OPTIONS.map(s => (
                                  <option key={s} value={s}>{s}</option>
                                ))}
                              </select>
                            </div>
                          )}
                          <div className="flex flex-col items-start gap-1">
                            <label className="block text-xs text-gray-500 mb-1 invisible">Save</label>
                            <button type="button" onClick={() => saveQuestion(q.id)} disabled={r.saving}
                              className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50 transition">
                              {r.saving ? "Saving…" : "Save"}
                            </button>
                          </div>
                        </div>
                      )}

                    </div>

                    {r.err && (
                      <div className="mt-2 text-xs text-red-600">{r.err}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Footer actions */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={() => navigate("/templates")}
          className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50"
        >
          ← Back to templates
        </button>

        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">
            {totalCount - missingCount} / {totalCount} rules set
          </span>
          <button
            type="button"
            onClick={saveAll}
            disabled={savingAll}
            className="px-5 py-2 text-sm rounded-lg bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-50 transition"
          >
            {savingAll ? "Saving all…" : "Save all rules"}
          </button>
        </div>
      </div>
    </div>
  );
}
